import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Users, UserPlus, Shield, ShieldCheck, ShieldAlert, Trash2, Eye, DollarSign, Copy, RefreshCw, ChevronRight, Lock, AlertCircle, Settings, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function Badge({ children, color = "#6E7385" }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
      {children}
    </span>
  );
}

function PrivacyIcon({ level }) {
  if (level === "full") return <Lock className="w-4 h-4 text-emerald-400" />;
  if (level === "moderate") return <ShieldCheck className="w-4 h-4 text-amber-400" />;
  return <ShieldAlert className="w-4 h-4 text-red-400" />;
}

export default function GroupAdminPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [addForm, setAddForm] = useState({ username: "", email: "", preferred_name: "", date_of_birth: "", password: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [modulesMember, setModulesMember] = useState(null);
  const [modulesData, setModulesData] = useState(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesSaving, setModulesSaving] = useState(false);

  const token = localStorage.getItem("omni_token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/group/dashboard`, { headers });
      if (res.status === 403) {
        setError("Group Plan subscription required to access this page.");
        setLoading(false);
        return;
      }
      const d = await res.json();
      setData(d);
    } catch {
      setError("Failed to load group dashboard.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!token) { navigate("/auth"); return; }
    fetchDashboard();
  }, [token, navigate, fetchDashboard]);

  const addMember = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      const res = await fetch(`${API}/api/group/members/add`, {
        method: "POST", headers, body: JSON.stringify(addForm),
      });
      const d = await res.json();
      if (!res.ok) { alert(d.detail || "Failed to add member"); setAddLoading(false); return; }
      setShowAddMember(false);
      setAddForm({ username: "", email: "", preferred_name: "", date_of_birth: "", password: "" });
      fetchDashboard();
    } catch { alert("Network error"); }
    setAddLoading(false);
  };

  const removeMember = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from the group? They will be downgraded to the Free plan.`)) return;
    try {
      const res = await fetch(`${API}/api/group/members/${memberId}`, { method: "DELETE", headers });
      if (res.ok) fetchDashboard();
      else { const d = await res.json(); alert(d.detail || "Failed"); }
    } catch { alert("Network error"); }
  };

  const addExtraSlot = async () => {
    setAddonLoading(true);
    try {
      const res = await fetch(`${API}/api/group/addons/member`, { method: "POST", headers });
      const d = await res.json();
      if (res.ok) { alert(d.message); fetchDashboard(); }
      else alert(d.detail || "Failed");
    } catch { alert("Network error"); }
    setAddonLoading(false);
  };

  const viewActivity = async (memberId) => {
    setSelectedMember(memberId);
    setActivityLoading(true);
    try {
      const res = await fetch(`${API}/api/group/members/${memberId}/activity`, { headers });
      const d = await res.json();
      setActivityData(d);
    } catch { setActivityData(null); }
    setActivityLoading(false);
  };

  const openModules = async (memberId) => {
    setModulesMember(memberId);
    setModulesLoading(true);
    try {
      const res = await fetch(`${API}/api/group/members/${memberId}/modules`, { headers });
      const d = await res.json();
      setModulesData(d);
    } catch { setModulesData(null); }
    setModulesLoading(false);
  };

  const toggleModule = (mod) => {
    if (!modulesData) return;
    const current = modulesData.allowed_modules || [];
    if (mod === "friend") return; // Always required
    const updated = current.includes(mod) ? current.filter(m => m !== mod) : [...current, mod];
    setModulesData({ ...modulesData, allowed_modules: updated });
  };

  const saveModules = async () => {
    if (!modulesData || !modulesMember) return;
    setModulesSaving(true);
    try {
      const res = await fetch(`${API}/api/group/members/${modulesMember}/modules`, {
        method: "PUT", headers, body: JSON.stringify({ modules: modulesData.allowed_modules }),
      });
      const d = await res.json();
      if (res.ok) { alert(d.message); fetchDashboard(); setModulesMember(null); setModulesData(null); }
      else alert(d.detail || "Failed to update modules");
    } catch { alert("Network error"); }
    setModulesSaving(false);
  };

  const copyInvite = () => {
    if (data?.group?.invite_code) {
      navigator.clipboard.writeText(data.group.invite_code);
      alert("Invite code copied!");
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#060912" }}>
      <RefreshCw className="w-6 h-6 text-[#E31212] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#060912" }}>
      <div className="text-center max-w-md">
        <AlertCircle className="w-12 h-12 text-[#E31212] mx-auto mb-4" />
        <p className="text-white text-lg mb-2">Access Restricted</p>
        <p className="text-[#6E7385] text-sm mb-6">{error}</p>
        <button onClick={() => navigate("/settings")} className="px-6 py-3 rounded-full text-sm font-medium text-white"
          style={{ background: "#E31212" }}>
          Go to Settings
        </button>
      </div>
    </div>
  );

  const isAdmin = data?.is_admin;
  const cap = data?.capacity || {};
  const bill = data?.billing || {};

  return (
    <div className="min-h-screen" style={{ background: "#060912", color: "#fff" }} data-testid="group-admin-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "rgba(227,18,18,0.1)" }}>
              <Crown className="w-6 h-6 text-[#E31212]" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Raleway', sans-serif" }}>
                {data?.group?.name || "Group"} Admin
              </h1>
              <p className="text-sm text-[#6E7385]">Group Plan · Bi-annual billing</p>
            </div>
          </div>
          {isAdmin && data?.group?.invite_code && (
            <button onClick={copyInvite} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-medium border transition-all hover:bg-white/5"
              style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid="copy-invite-btn">
              <Copy className="w-3.5 h-3.5" /> {data.group.invite_code}
            </button>
          )}
        </div>

        {/* Capacity + Billing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
            <Users className="w-5 h-5 text-[#E31212] mb-3" />
            <p className="text-2xl font-semibold">{cap.used_slots || 0}<span className="text-sm text-[#6E7385] font-normal">/{cap.total_slots || 6}</span></p>
            <p className="text-xs text-[#6E7385] mt-1">Members ({cap.base_slots || 6} base + {cap.addon_slots || 0} add-on)</p>
          </div>
          <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
            <DollarSign className="w-5 h-5 text-[#F5A623] mb-3" />
            <p className="text-2xl font-semibold">${bill.total_biannual || "209.94"}<span className="text-sm text-[#6E7385] font-normal">/6mo</span></p>
            <p className="text-xs text-[#6E7385] mt-1">${bill.total_monthly || "34.99"}/mo · Billed bi-annually</p>
          </div>
          <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
            <Shield className="w-5 h-5 text-emerald-400 mb-3" />
            <p className="text-lg font-semibold">Privacy Active</p>
            <p className="text-xs text-[#6E7385] mt-1">Individual privacy per member based on age</p>
          </div>
        </div>

        {/* Members List */}
        <div className="rounded-2xl border overflow-hidden mb-8" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
          <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <h2 className="text-lg font-medium flex items-center gap-2"><Users className="w-5 h-5 text-[#E31212]" /> Group Members</h2>
            {isAdmin && (
              <button onClick={() => setShowAddMember(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "#E31212", color: "#fff" }} data-testid="add-member-btn">
                <UserPlus className="w-3.5 h-3.5" /> Add Member
              </button>
            )}
          </div>

          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {(data?.members || []).map((m) => (
              <div key={m.user_id} className="flex items-center justify-between p-5 hover:bg-white/[0.02] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                    style={{ background: m.role === "guardian" ? "rgba(227,18,18,0.12)" : "rgba(255,255,255,0.05)", color: m.role === "guardian" ? "#E31212" : "#A1A5B3" }}>
                    {(m.name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white flex items-center gap-2">
                      {m.name}
                      {m.role === "guardian" && <Badge color="#E31212">Admin</Badge>}
                      {m.is_addon_member && <Badge color="#F5A623">Add-on</Badge>}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-[11px] text-[#6E7385]">
                        <PrivacyIcon level={m.privacy_level} />
                        {m.privacy_label}
                      </span>
                      {m.age !== null && m.age !== undefined && (
                        <span className="text-[11px] text-[#6E7385]">Age: {m.age}</span>
                      )}
                      {m.is_underage && m.allowed_modules && (
                        <span className="text-[11px] text-[#F5A623]">
                          {m.allowed_modules.length}/{Object.keys(data?.module_info || {}).length} modules
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && m.role !== "guardian" && (
                    <>
                      {m.is_underage && (
                        <button onClick={() => openModules(m.user_id)} className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                          title="Manage modules" data-testid={`manage-modules-${m.user_id}`}>
                          <Settings className="w-4 h-4 text-[#F5A623]" />
                        </button>
                      )}
                      <button onClick={() => viewActivity(m.user_id)} className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                        title="View activity" data-testid={`view-activity-${m.user_id}`}>
                        <Eye className="w-4 h-4 text-[#6E7385]" />
                      </button>
                      <button onClick={() => removeMember(m.user_id, m.name)} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                        title="Remove member" data-testid={`remove-member-${m.user_id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </>
                  )}
                  {m.role === "guardian" && (
                    <span className="text-[11px] text-[#6E7385]">You</span>
                  )}
                </div>
              </div>
            ))}
            {(data?.members || []).length === 0 && (
              <div className="p-10 text-center">
                <Users className="w-8 h-8 text-[#6E7385] mx-auto mb-3" />
                <p className="text-sm text-[#6E7385]">No members yet. Add your first group member.</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Extra Slot */}
        {isAdmin && cap.available_slots <= 1 && (
          <div className="rounded-2xl p-6 border mb-8 flex items-center justify-between flex-wrap gap-4"
            style={{ background: "rgba(245,166,35,0.03)", borderColor: "rgba(245,166,35,0.15)" }}>
            <div>
              <p className="text-sm font-medium text-white mb-1">Need more member slots?</p>
              <p className="text-xs text-[#6E7385]">Add extra members at $7.99/mo each ($47.94/6mo), billed with your group subscription.</p>
            </div>
            <button onClick={addExtraSlot} disabled={addonLoading}
              className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50"
              style={{ background: "#F5A623", color: "#060912" }} data-testid="add-slot-btn">
              <UserPlus className="w-4 h-4" /> {addonLoading ? "Adding..." : "Add Member Slot"}
            </button>
          </div>
        )}

        {/* Privacy Levels Legend */}
        <div className="rounded-2xl border p-6 mb-8" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
          <h3 className="text-base font-medium mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-[#E31212]" /> Privacy Levels</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(data?.privacy_levels || {}).map(([key, info]) => (
              <div key={key} className="p-4 rounded-xl border" style={{ borderColor: "rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.01)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <PrivacyIcon level={key} />
                  <p className="text-sm font-medium">{info.label}</p>
                </div>
                <p className="text-[11px] text-[#6E7385] leading-relaxed">{info.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Billing Breakdown */}
        <div className="rounded-2xl border p-6" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
          <h3 className="text-base font-medium mb-4 flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#F5A623]" /> Billing Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
              <span className="text-sm text-[#A1A5B3]">Group Plan Base (6 members)</span>
              <span className="text-sm font-medium">${bill.base_biannual || "209.94"}/6mo</span>
            </div>
            {(bill.addon_members_count || 0) > 0 && (
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                <span className="text-sm text-[#A1A5B3]">Extra Members ({bill.addon_members_count} x $47.94)</span>
                <span className="text-sm font-medium">${((bill.addon_members_count || 0) * 47.94).toFixed(2)}/6mo</span>
              </div>
            )}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-medium text-white">Total (bi-annual)</span>
              <span className="text-lg font-semibold text-[#E31212]">${bill.total_biannual || "209.94"}/6mo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowAddMember(false)}>
          <div className="w-full max-w-md rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.1)" }}
            onClick={(e) => e.stopPropagation()} data-testid="add-member-modal">
            <h3 className="text-lg font-medium mb-1">Add Group Member</h3>
            <p className="text-xs text-[#6E7385] mb-6">Privacy level will be auto-assigned based on date of birth.</p>
            <form onSubmit={addMember} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6E7385] mb-1.5">Username *</label>
                <input type="text" required value={addForm.username} onChange={(e) => setAddForm({ ...addForm, username: e.target.value })}
                  className="w-full h-11 px-4 rounded-xl text-sm text-white outline-none"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="add-member-username" placeholder="johndoe" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6E7385] mb-1.5">Email</label>
                <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-xl text-sm text-white outline-none"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="add-member-email" placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6E7385] mb-1.5">Preferred Name</label>
                <input type="text" value={addForm.preferred_name} onChange={(e) => setAddForm({ ...addForm, preferred_name: e.target.value })}
                  className="w-full h-11 px-4 rounded-xl text-sm text-white outline-none"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="add-member-name" placeholder="John" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6E7385] mb-1.5">Date of Birth *</label>
                <input type="date" required value={addForm.date_of_birth} onChange={(e) => setAddForm({ ...addForm, date_of_birth: e.target.value })}
                  className="w-full h-11 px-4 rounded-xl text-sm text-white outline-none"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="add-member-dob" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6E7385] mb-1.5">Password</label>
                <input type="password" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-xl text-sm text-white outline-none"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="add-member-password" placeholder="Leave blank for default" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddMember(false)}
                  className="flex-1 py-3 rounded-full text-sm font-medium border transition-all hover:bg-white/5"
                  style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  Cancel
                </button>
                <button type="submit" disabled={addLoading}
                  className="flex-1 py-3 rounded-full text-sm font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ background: "#E31212" }} data-testid="add-member-submit">
                  {addLoading ? "Adding..." : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setSelectedMember(null); setActivityData(null); }}>
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.1)" }}
            onClick={(e) => e.stopPropagation()} data-testid="activity-modal">
            {activityLoading ? (
              <div className="py-10 text-center"><RefreshCw className="w-5 h-5 text-[#E31212] animate-spin mx-auto" /></div>
            ) : activityData ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-medium">{activityData.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <PrivacyIcon level={activityData.privacy_level} />
                      <span className="text-xs text-[#6E7385]">{activityData.privacy_label}</span>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedMember(null); setActivityData(null); }} className="text-[#6E7385] hover:text-white transition-colors text-xl">&times;</button>
                </div>

                {activityData.privacy_level === "full" && (
                  <div className="p-4 rounded-xl mb-4" style={{ background: "rgba(52,211,153,0.05)", border: "1px solid rgba(52,211,153,0.15)" }}>
                    <p className="text-sm text-emerald-400 flex items-center gap-2"><Lock className="w-4 h-4" /> Full privacy active</p>
                    <p className="text-xs text-[#6E7385] mt-1">This member is an adult with full privacy. Only membership status is visible.</p>
                  </div>
                )}

                <div className="space-y-3">
                  {activityData.membership_status && (
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                      <span className="text-sm text-[#6E7385]">Status</span>
                      <Badge color="#34D399">{activityData.membership_status}</Badge>
                    </div>
                  )}
                  {activityData.last_active && (
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                      <span className="text-sm text-[#6E7385]">Last Active</span>
                      <span className="text-sm">{new Date(activityData.last_active).toLocaleDateString()}</span>
                    </div>
                  )}
                  {activityData.mood_summary && (
                    <div className="py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                      <p className="text-sm text-[#6E7385] mb-2">Mood Summary (14 days)</p>
                      <div className="flex gap-4">
                        <div><p className="text-lg font-semibold">{activityData.mood_summary.avg_score || "—"}</p><p className="text-[10px] text-[#6E7385]">Avg Score</p></div>
                        <div><p className="text-lg font-semibold">{activityData.mood_summary.entries}</p><p className="text-[10px] text-[#6E7385]">Entries</p></div>
                      </div>
                    </div>
                  )}
                  {activityData.activity_summary && (
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                      <span className="text-sm text-[#6E7385]">Conversations (14d)</span>
                      <span className="text-sm font-medium">{activityData.activity_summary.conversations_14d}</span>
                    </div>
                  )}
                  {activityData.module_usage && Object.keys(activityData.module_usage).length > 0 && (
                    <div className="py-2">
                      <p className="text-sm text-[#6E7385] mb-2">Module Usage</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(activityData.module_usage).map(([mod, count]) => (
                          <Badge key={mod} color="#A78BFA">{mod}: {count}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {activityData.recent_conversations && (
                    <div className="py-2">
                      <p className="text-sm text-[#6E7385] mb-2">Recent Conversations (supervised)</p>
                      {activityData.recent_conversations.map((c) => (
                        <div key={c.id} className="p-3 rounded-lg mb-2" style={{ background: "rgba(255,255,255,0.02)" }}>
                          <p className="text-sm font-medium">{c.title}</p>
                          <p className="text-[10px] text-[#6E7385]">{c.module} · {new Date(c.updated_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-[#6E7385] text-center py-10">No activity data available.</p>
            )}
          </div>
        </div>
      )}
      {/* Module Management Modal */}
      {modulesMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setModulesMember(null); setModulesData(null); }}>
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.1)" }}
            onClick={(e) => e.stopPropagation()} data-testid="modules-modal">
            {modulesLoading ? (
              <div className="py-10 text-center"><RefreshCw className="w-5 h-5 text-[#F5A623] animate-spin mx-auto" /></div>
            ) : modulesData ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-medium flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#F5A623]" /> Module Access</h3>
                    <p className="text-xs text-[#6E7385] mt-1">{modulesData.name} — Age {modulesData.age}</p>
                  </div>
                  <button onClick={() => { setModulesMember(null); setModulesData(null); }} className="text-[#6E7385] hover:text-white transition-colors text-xl">&times;</button>
                </div>

                {!modulesData.can_admin_edit && (
                  <div className="p-4 rounded-xl mb-4" style={{ background: "rgba(52,211,153,0.05)", border: "1px solid rgba(52,211,153,0.15)" }}>
                    <p className="text-sm text-emerald-400 flex items-center gap-2"><Lock className="w-4 h-4" /> Adult member</p>
                    <p className="text-xs text-[#6E7385] mt-1">Adults have full access to all modules. Module restrictions only apply to underage members.</p>
                  </div>
                )}

                <div className="space-y-2">
                  {Object.entries(modulesData.available_modules || {}).map(([mod, info]) => {
                    const enabled = (modulesData.allowed_modules || []).includes(mod);
                    const eligible = info.age_eligible;
                    const isRequired = mod === "friend";
                    return (
                      <div key={mod} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${enabled ? "" : "opacity-60"}`}
                        style={{ borderColor: enabled ? "rgba(245,166,35,0.2)" : "rgba(255,255,255,0.05)", background: enabled ? "rgba(245,166,35,0.03)" : "rgba(255,255,255,0.01)" }}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium flex items-center gap-2">
                            {info.name}
                            {isRequired && <Badge color="#34D399">Required</Badge>}
                            {!eligible && <Badge color="#E31212">Age {info.min_age}+</Badge>}
                          </p>
                          <p className="text-[11px] text-[#6E7385] mt-0.5">{info.desc}</p>
                        </div>
                        {modulesData.can_admin_edit && (
                          <button
                            onClick={() => eligible && !isRequired && toggleModule(mod)}
                            disabled={!eligible || isRequired}
                            className={`w-11 h-6 rounded-full transition-all flex-shrink-0 ml-3 relative ${enabled ? "" : ""}`}
                            style={{ background: enabled ? "#F5A623" : "rgba(255,255,255,0.1)", opacity: (!eligible || isRequired) ? 0.3 : 1 }}
                            data-testid={`toggle-module-${mod}`}>
                            <div className="w-4 h-4 rounded-full bg-white absolute top-1 transition-all"
                              style={{ left: enabled ? "calc(100% - 20px)" : "4px" }} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {modulesData.can_admin_edit && (
                  <div className="flex gap-3 pt-5">
                    <button onClick={() => { setModulesMember(null); setModulesData(null); }}
                      className="flex-1 py-3 rounded-full text-sm font-medium border transition-all hover:bg-white/5"
                      style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      Cancel
                    </button>
                    <button onClick={saveModules} disabled={modulesSaving}
                      className="flex-1 py-3 rounded-full text-sm font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                      style={{ background: "#F5A623", color: "#060912" }} data-testid="save-modules-btn">
                      {modulesSaving ? "Saving..." : `Save (${(modulesData.allowed_modules || []).length} modules)`}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-[#6E7385] text-center py-10">Failed to load module data.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
